import {
  CalendarDays,
  ChevronRight,
  Clock3,
  Download,
  FileAudio,
  ListFilter,
  ListChecks,
  LoaderCircle,
  MessagesSquare,
  Pencil,
  Play,
  RefreshCw,
  Save,
  Search,
  SearchX,
  Sparkles,
  TriangleAlert,
  UsersRound,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getMeetingArchive,
  listMeetings,
  meetingAudioDownloadUrl,
  meetingAudioUrl,
  meetingSummaryDownloadUrl,
  meetingTranscriptDownloadUrl,
  renameMeetingSpeaker,
  retryMeetingProcessing,
  type ArchiveLocale,
  type MeetingArchive as MeetingArchiveRecord,
  type MeetingRecord,
  type MeetingSummaryContent,
} from "../lib/meetingApi";
import type { TargetLanguage } from "../shared/languages";
import { stripSummaryEvidence } from "../shared/summaryEvidence";

export type MeetingArchiveCopy = {
  title: string;
  description: string;
  refresh: string;
  loading: string;
  loadError: string;
  empty: string;
  noResults: string;
  selectPrompt: string;
  detailError: string;
  retry: string;
  searchLabel: string;
  searchPlaceholder: string;
  statusFilterLabel: string;
  statusAll: string;
  audioTitle: string;
  audioUnavailable: string;
  downloadAudio: string;
  processingTitle: string;
  processingQueued: string;
  processingRunning: string;
  processingReady: string;
  processingFailed: string;
  processingError: string;
  retryProcessing: string;
  retryingProcessing: string;
  transcriptTitle: string;
  transcriptPending: string;
  transcriptEmpty: string;
  downloadTranscript: string;
  viewTranscript: string;
  backToArchive: string;
  playFromTimestamp: string;
  renameSpeaker: string;
  speakerNameLabel: string;
  saveSpeaker: string;
  cancel: string;
  speakerSaveError: string;
  summaryTitle: string;
  summaryPending: string;
  summaryEmpty: string;
  downloadSummary: string;
  overview: string;
  keyPoints: string;
  decisions: string;
  actionItems: string;
  openQuestions: string;
  statusRecording: string;
  statusFinalizing: string;
  statusReady: string;
  statusFailed: string;
  statusProcessing: string;
  localeEnglish: string;
  localeIndonesian: string;
  localeChinese: string;
  localeThai: string;
};

export type MeetingArchiveProps = {
  language: TargetLanguage;
  copy: MeetingArchiveCopy;
  csrfToken: string;
  initialMeetingId?: string;
};

export type MeetingTranscriptViewProps = {
  meetingId: string;
  language: TargetLanguage;
  copy: MeetingArchiveCopy;
  csrfToken: string;
};

type RequestState = "idle" | "loading" | "ready" | "error";

const SUMMARY_LOCALES: ArchiveLocale[] = ["en", "id", "zh", "th"];
const POLL_INTERVAL_MS = 5_000;

export function MeetingArchive({
  language,
  copy,
  csrfToken,
  initialMeetingId,
}: MeetingArchiveProps) {
  const [meetings, setMeetings] = useState<MeetingRecord[]>([]);
  const [listState, setListState] = useState<RequestState>("loading");
  const [selectedId, setSelectedId] = useState<string | null>(initialMeetingId ?? null);
  const [archive, setArchive] = useState<MeetingArchiveRecord | null>(null);
  const [detailState, setDetailState] = useState<RequestState>("idle");
  const [summaryLocale, setSummaryLocale] = useState<ArchiveLocale>(language);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const listOperationRef = useRef(0);
  const detailOperationRef = useRef(0);

  const loadArchive = useCallback(async (meetingId: string, quiet = false) => {
    const operation = ++detailOperationRef.current;
    if (!quiet) {
      setSelectedId(meetingId);
      setArchive(null);
      setDetailState("loading");
    }
    try {
      const result = await getMeetingArchive(meetingId);
      if (detailOperationRef.current !== operation) return;
      setArchive(result);
      setMeetings(current => current.map(meeting => (
        meeting.id === result.meeting.id ? { ...meeting, ...result.meeting } : meeting
      )));
      setDetailState("ready");
    } catch {
      if (detailOperationRef.current !== operation) return;
      if (!quiet) setDetailState("error");
    }
  }, []);

  const loadMeetings = useCallback(async () => {
    const operation = ++listOperationRef.current;
    setListState("loading");
    try {
      const result = await listMeetings();
      if (listOperationRef.current !== operation) return;
      const ordered = [...result].sort((left, right) => right.createdAt - left.createdAt);
      setMeetings(ordered);
      setListState("ready");
      const nextSelected = ordered.some(meeting => meeting.id === selectedId)
        ? selectedId
        : ordered[0]?.id ?? null;
      if (!nextSelected) {
        detailOperationRef.current += 1;
        setSelectedId(null);
        setArchive(null);
        setDetailState("idle");
      } else {
        await loadArchive(nextSelected);
      }
    } catch {
      if (listOperationRef.current !== operation) return;
      setListState("error");
    }
  }, [loadArchive, selectedId]);

  useEffect(() => {
    void loadMeetings();
    return () => {
      listOperationRef.current += 1;
      detailOperationRef.current += 1;
    };
    // Load once on authenticated mount. Refresh remains explicit after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSummaryLocale(language);
  }, [language]);

  const processingState = archive ? normalizedProcessingState(archive.processing.state) : null;
  useEffect(() => {
    if (
      !selectedId
      || !archive
      || (
        processingState !== "queued"
        && processingState !== "processing"
        && processingState !== "ready"
      )
    ) return;
    if (processingState === "ready" && archive.meeting.playbackReady) return;
    const interval = window.setInterval(() => {
      void loadArchive(selectedId, true);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [archive, loadArchive, processingState, selectedId]);

  const selectedMeeting = meetings.find(meeting => meeting.id === selectedId) ?? null;
  const filteredMeetings = useMemo(() => {
    const query = normalizedSearch(searchQuery);
    return meetings.filter(meeting => {
      const state = normalizedMeetingState(meeting.state);
      if (statusFilter !== "all" && state !== statusFilter) return false;
      if (!query) return true;
      const searchable = [
        meeting.title,
        formatMeetingDate(meeting.createdAt, language),
        meetingStateLabel(meeting.state, copy),
      ].map(normalizedSearch);
      return searchable.some(value => value.includes(query));
    });
  }, [copy, language, meetings, searchQuery, statusFilter]);

  const updateProcessing = useCallback((processing: MeetingArchiveRecord["processing"]) => {
    setArchive(current => current ? { ...current, processing } : current);
  }, []);

  return (
    <section className="meeting-archive" aria-labelledby="meeting-archive-title">
      <header className="meeting-archive__header">
        <div>
          <p className="eyebrow">{copy.title}</p>
          <h2 id="meeting-archive-title">{copy.title}</h2>
          <p>{copy.description}</p>
        </div>
        <button
          className="secondary-button"
          type="button"
          disabled={listState === "loading"}
          aria-busy={listState === "loading"}
          onClick={() => void loadMeetings()}
        >
          <RefreshCw
            className={listState === "loading" ? "progress-spinner" : undefined}
            size={17}
            aria-hidden="true"
          />
          {copy.refresh}
        </button>
      </header>

      {listState === "loading" && meetings.length === 0 ? (
        <ArchiveMessage icon={<LoaderCircle className="progress-spinner" />} message={copy.loading} />
      ) : listState === "error" && meetings.length === 0 ? (
        <ArchiveMessage
          icon={<TriangleAlert />}
          message={copy.loadError}
          tone="error"
          action={(
            <button className="secondary-button" type="button" onClick={() => void loadMeetings()}>
              <RefreshCw size={16} aria-hidden="true" />
              {copy.retry}
            </button>
          )}
        />
      ) : meetings.length === 0 ? (
        <ArchiveMessage icon={<CalendarDays />} message={copy.empty} />
      ) : (
        <div className="meeting-archive__workspace">
          <div className="meeting-archive__sidebar">
            <div className="meeting-archive__filters">
              <label className="archive-search">
                <span className="sr-only">{copy.searchLabel}</span>
                <Search size={17} aria-hidden="true" />
                <input
                  type="search"
                  value={searchQuery}
                  placeholder={copy.searchPlaceholder}
                  aria-label={copy.searchLabel}
                  onChange={event => setSearchQuery(event.currentTarget.value)}
                />
              </label>
              <label className="archive-filter">
                <span className="sr-only">{copy.statusFilterLabel}</span>
                <ListFilter size={17} aria-hidden="true" />
                <select
                  value={statusFilter}
                  aria-label={copy.statusFilterLabel}
                  onChange={event => setStatusFilter(event.currentTarget.value)}
                >
                  <option value="all">{copy.statusAll}</option>
                  <option value="recording">{copy.statusRecording}</option>
                  <option value="finalizing">{copy.statusFinalizing}</option>
                  <option value="processing">{copy.statusProcessing}</option>
                  <option value="ready">{copy.statusReady}</option>
                  <option value="failed">{copy.statusFailed}</option>
                </select>
              </label>
            </div>
            <nav className="meeting-archive__list" aria-label={copy.title}>
            {filteredMeetings.map(meeting => (
              <button
                key={meeting.id}
                className="meeting-archive__meeting"
                type="button"
                aria-current={selectedId === meeting.id ? "true" : undefined}
                onClick={() => void loadArchive(meeting.id)}
              >
                <span className="meeting-archive__meeting-title">{meeting.title}</span>
                <span className="meeting-archive__meeting-meta">
                  <CalendarDays size={14} aria-hidden="true" />
                  <time dateTime={new Date(meeting.createdAt).toISOString()}>
                    {formatMeetingDate(meeting.createdAt, language)}
                  </time>
                  <MeetingState state={meeting.state} copy={copy} />
                </span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
            ))}
              {filteredMeetings.length === 0 && (
                <div className="meeting-archive__no-results" role="status">
                  <SearchX size={19} aria-hidden="true" />
                  <p>{copy.noResults}</p>
                </div>
              )}
            </nav>
          </div>

          <div className="meeting-archive__detail" aria-live="polite">
            {!selectedMeeting ? (
              <ArchiveMessage icon={<ChevronRight />} message={copy.selectPrompt} />
            ) : detailState === "loading" ? (
              <ArchiveMessage icon={<LoaderCircle className="progress-spinner" />} message={copy.loading} />
            ) : detailState === "error" ? (
              <ArchiveMessage
                icon={<TriangleAlert />}
                message={copy.detailError}
                tone="error"
                action={(
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => void loadArchive(selectedMeeting.id)}
                  >
                    <RefreshCw size={16} aria-hidden="true" />
                    {copy.retry}
                  </button>
                )}
              />
            ) : archive ? (
              <ArchiveDetail
                archive={archive}
                language={language}
                copy={copy}
                csrfToken={csrfToken}
                summaryLocale={summaryLocale}
                onSummaryLocaleChange={setSummaryLocale}
                onProcessingChange={updateProcessing}
                onRefresh={() => loadArchive(archive.meeting.id, true)}
              />
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}

function ArchiveDetail({
  archive,
  language,
  copy,
  csrfToken,
  summaryLocale,
  onSummaryLocaleChange,
  onProcessingChange,
  onRefresh,
}: {
  archive: MeetingArchiveRecord;
  language: TargetLanguage;
  copy: MeetingArchiveCopy;
  csrfToken: string;
  summaryLocale: ArchiveLocale;
  onSummaryLocaleChange(locale: ArchiveLocale): void;
  onProcessingChange(processing: MeetingArchiveRecord["processing"]): void;
  onRefresh(): Promise<void>;
}) {
  const summary = archive.summaries.find(candidate => candidate.locale === summaryLocale) ?? null;
  const processingState = normalizedProcessingState(archive.processing.state);
  const processingReady = processingState === "ready";
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(false);

  const retryProcessing = async () => {
    setRetrying(true);
    setRetryError(false);
    try {
      const processing = await retryMeetingProcessing(csrfToken, archive.meeting.id);
      onProcessingChange(processing);
      await onRefresh();
    } catch {
      setRetryError(true);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <article className="archive-detail">
      <header className="archive-detail__header">
        <div>
          <h3>{archive.meeting.title}</h3>
          <p>
            <CalendarDays size={15} aria-hidden="true" />
            <time dateTime={new Date(archive.meeting.createdAt).toISOString()}>
              {formatMeetingDate(archive.meeting.createdAt, language)}
            </time>
          </p>
        </div>
        <MeetingState state={archive.meeting.state} copy={copy} />
      </header>

      <section className="archive-card" aria-labelledby="archive-audio-title">
        <div className="archive-card__heading">
          <h4 id="archive-audio-title"><FileAudio size={18} aria-hidden="true" />{copy.audioTitle}</h4>
          <DownloadControl
            available={Boolean(archive.meeting.playbackReady)}
            href={meetingAudioDownloadUrl(archive.meeting.id)}
            label={copy.downloadAudio}
          />
        </div>
        {archive.meeting.playbackReady ? (
          <audio controls preload="metadata" src={meetingAudioUrl(archive.meeting.id)}>
            {copy.audioUnavailable}
          </audio>
        ) : (
          <p className="archive-card__empty">{copy.audioUnavailable}</p>
        )}
      </section>

      <section className="archive-card" aria-labelledby="archive-processing-title">
        <h4 id="archive-processing-title"><Sparkles size={18} aria-hidden="true" />{copy.processingTitle}</h4>
        <p className={`archive-processing archive-processing--${processingState}`}>
          {processingState === "processing" && <LoaderCircle className="progress-spinner" size={17} aria-hidden="true" />}
          {processingState === "failed" && <TriangleAlert size={17} aria-hidden="true" />}
          {processingStatusLabel(processingState, copy)}
        </p>
        {archive.processing.lastError && processingState === "failed" && (
          <p className="archive-processing__error">{copy.processingError}</p>
        )}
        {processingState === "failed" && archive.processing.canRetry && (
          <button
            className="secondary-button archive-processing__retry"
            type="button"
            disabled={retrying}
            aria-busy={retrying}
            onClick={() => void retryProcessing()}
          >
            <RefreshCw
              className={retrying ? "progress-spinner" : undefined}
              size={16}
              aria-hidden="true"
            />
            {retrying ? copy.retryingProcessing : copy.retryProcessing}
          </button>
        )}
        {retryError && <p className="archive-processing__error" role="alert">{copy.processingError}</p>}
      </section>

      <section className="archive-card" aria-labelledby="archive-transcript-title">
        <div className="archive-card__heading">
          <h4 id="archive-transcript-title"><MessagesSquare size={18} aria-hidden="true" />{copy.transcriptTitle}</h4>
          <a
            className="archive-transcript-link"
            href={`/host/archive/${encodeURIComponent(archive.meeting.id)}/transcript?lang=${language}`}
          >
            <MessagesSquare size={16} aria-hidden="true" />
            <span>{copy.viewTranscript}</span>
            <ChevronRight size={16} aria-hidden="true" />
          </a>
        </div>
        {!archive.transcript ? (
          <p className="archive-card__empty">
            {processingReady ? copy.transcriptEmpty : copy.transcriptPending}
          </p>
        ) : archive.transcript.segments.length === 0 && (
          <p className="archive-card__empty">{copy.transcriptEmpty}</p>
        )}
      </section>

      <section className="archive-card" aria-labelledby="archive-summary-title">
        <h4 id="archive-summary-title"><ListChecks size={18} aria-hidden="true" />{copy.summaryTitle}</h4>
        <div className="archive-downloads" aria-label={copy.downloadSummary}>
          {SUMMARY_LOCALES.map(locale => (
            <DownloadControl
              key={locale}
              available={archive.summaries.some(candidate => candidate.locale === locale)}
              href={meetingSummaryDownloadUrl(archive.meeting.id, locale)}
              label={`${copy.downloadSummary} · ${localeLabel(locale, copy)}`}
              compact
            />
          ))}
        </div>
        <div className="archive-summary__tabs" role="tablist" aria-label={copy.summaryTitle}>
          {SUMMARY_LOCALES.map(locale => (
            <button
              key={locale}
              id={`archive-summary-tab-${locale}`}
              type="button"
              role="tab"
              aria-selected={summaryLocale === locale}
              aria-controls={`archive-summary-panel-${locale}`}
              onClick={() => onSummaryLocaleChange(locale)}
            >
              {localeLabel(locale, copy)}
            </button>
          ))}
        </div>
        <div
          id={`archive-summary-panel-${summaryLocale}`}
          className="archive-summary__panel"
          role="tabpanel"
          aria-labelledby={`archive-summary-tab-${summaryLocale}`}
          tabIndex={0}
        >
          {summary ? (
            <SummaryContent content={summary.content} copy={copy} />
          ) : (
            <p className="archive-card__empty">
              {processingReady ? copy.summaryEmpty : copy.summaryPending}
            </p>
          )}
        </div>
      </section>
    </article>
  );
}

export function MeetingTranscriptView({
  meetingId,
  language,
  copy,
  csrfToken,
}: MeetingTranscriptViewProps) {
  const [archive, setArchive] = useState<MeetingArchiveRecord | null>(null);
  const [state, setState] = useState<RequestState>("loading");
  const operationRef = useRef(0);

  const loadArchive = useCallback(async (quiet = false) => {
    const operation = ++operationRef.current;
    if (!quiet) setState("loading");
    try {
      const result = await getMeetingArchive(meetingId);
      if (operationRef.current !== operation) return;
      setArchive(result);
      setState("ready");
    } catch {
      if (operationRef.current !== operation) return;
      if (!quiet) setState("error");
    }
  }, [meetingId]);

  useEffect(() => {
    void loadArchive();
    return () => {
      operationRef.current += 1;
    };
  }, [loadArchive]);

  const processingState = archive ? normalizedProcessingState(archive.processing.state) : null;
  useEffect(() => {
    if (
      !archive
      || (processingState !== "queued" && processingState !== "processing")
    ) return;
    const interval = window.setInterval(() => {
      void loadArchive(true);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [archive, loadArchive, processingState]);

  const updateSpeakerLabel = useCallback((sourceSpeakerLabel: string, displayName: string) => {
    setArchive(current => {
      if (!current?.transcript) return current;
      return {
        ...current,
        transcript: {
          ...current.transcript,
          segments: current.transcript.segments.map(segment => (
            segment.sourceSpeakerLabel === sourceSpeakerLabel
              ? { ...segment, speakerLabel: displayName }
              : segment
          )),
        },
      };
    });
  }, []);

  return (
    <section className="meeting-archive meeting-transcript-view" aria-labelledby="meeting-transcript-title">
      <header className="meeting-transcript-view__page-header">
        <div>
          <p className="eyebrow">{copy.title}</p>
          <h2 id="meeting-transcript-title">{copy.transcriptTitle}</h2>
          {archive && <p>{archive.meeting.title}</p>}
        </div>
      </header>

      {state === "loading" ? (
        <ArchiveMessage icon={<LoaderCircle className="progress-spinner" />} message={copy.loading} />
      ) : state === "error" ? (
        <ArchiveMessage
          icon={<TriangleAlert />}
          message={copy.detailError}
          tone="error"
          action={(
            <button className="secondary-button" type="button" onClick={() => void loadArchive()}>
              <RefreshCw size={16} aria-hidden="true" />
              {copy.retry}
            </button>
          )}
        />
      ) : archive ? (
        <TranscriptDetail
          archive={archive}
          language={language}
          copy={copy}
          csrfToken={csrfToken}
          onSpeakerChange={updateSpeakerLabel}
        />
      ) : null}
    </section>
  );
}

function TranscriptDetail({
  archive,
  language,
  copy,
  csrfToken,
  onSpeakerChange,
}: {
  archive: MeetingArchiveRecord;
  language: TargetLanguage;
  copy: MeetingArchiveCopy;
  csrfToken: string;
  onSpeakerChange(sourceSpeakerLabel: string, displayName: string): void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const processingReady = normalizedProcessingState(archive.processing.state) === "ready";

  const playFrom = async (milliseconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, milliseconds / 1_000);
    await audio.play().catch(() => undefined);
  };

  return (
    <article className="archive-detail">
      <header className="archive-detail__header">
        <div>
          <h3>{archive.meeting.title}</h3>
          <p>
            <CalendarDays size={15} aria-hidden="true" />
            <time dateTime={new Date(archive.meeting.createdAt).toISOString()}>
              {formatMeetingDate(archive.meeting.createdAt, language)}
            </time>
          </p>
        </div>
        <MeetingState state={archive.meeting.state} copy={copy} />
      </header>

      <section className="archive-card" aria-labelledby="transcript-audio-title">
        <div className="archive-card__heading">
          <h4 id="transcript-audio-title"><FileAudio size={18} aria-hidden="true" />{copy.audioTitle}</h4>
          <DownloadControl
            available={Boolean(archive.meeting.playbackReady)}
            href={meetingAudioDownloadUrl(archive.meeting.id)}
            label={copy.downloadAudio}
          />
        </div>
        {archive.meeting.playbackReady ? (
          <audio ref={audioRef} controls preload="metadata" src={meetingAudioUrl(archive.meeting.id)}>
            {copy.audioUnavailable}
          </audio>
        ) : (
          <p className="archive-card__empty">{copy.audioUnavailable}</p>
        )}
      </section>

      <section className="archive-card" aria-labelledby="transcript-list-title">
        <div className="archive-card__heading">
          <h4 id="transcript-list-title"><MessagesSquare size={18} aria-hidden="true" />{copy.transcriptTitle}</h4>
          <DownloadControl
            available={Boolean(archive.transcript)}
            href={meetingTranscriptDownloadUrl(archive.meeting.id)}
            label={copy.downloadTranscript}
          />
        </div>
        {!archive.transcript ? (
          <p className="archive-card__empty">
            {processingReady ? copy.transcriptEmpty : copy.transcriptPending}
          </p>
        ) : archive.transcript.segments.length === 0 ? (
          <p className="archive-card__empty">{copy.transcriptEmpty}</p>
        ) : (
          <ol className="archive-transcript">
            {archive.transcript.segments.map(segment => (
              <li key={segment.id}>
                <div className="archive-transcript__meta">
                  <SpeakerEditor
                    meetingId={archive.meeting.id}
                    csrfToken={csrfToken}
                    sourceSpeakerLabel={segment.sourceSpeakerLabel}
                    speakerLabel={segment.speakerLabel}
                    copy={copy}
                    onSaved={onSpeakerChange}
                  />
                  <button
                    className="archive-transcript__timestamp"
                    type="button"
                    disabled={!archive.meeting.playbackReady}
                    aria-label={`${copy.playFromTimestamp} ${formatTimestamp(segment.startMs)}`}
                    onClick={() => void playFrom(segment.startMs)}
                  >
                    <Play size={13} aria-hidden="true" />
                    <span className="archive-transcript__time-range" aria-hidden="true">
                      <time>{formatTimestamp(segment.startMs)}</time>
                      <span>–</span>
                      <time>{formatTimestamp(segment.endMs)}</time>
                    </span>
                  </button>
                </div>
                <p>{segment.text}</p>
              </li>
            ))}
          </ol>
        )}
      </section>
    </article>
  );
}

function DownloadControl({
  available,
  href,
  label,
  compact = false,
}: {
  available: boolean;
  href: string;
  label: string;
  compact?: boolean;
}) {
  const className = `archive-download${compact ? " archive-download--compact" : ""}`;
  return available ? (
    <a className={className} href={href} download>
      <Download size={15} aria-hidden="true" />
      <span>{label}</span>
    </a>
  ) : (
    <button className={className} type="button" disabled>
      <Download size={15} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

function SpeakerEditor({
  meetingId,
  csrfToken,
  sourceSpeakerLabel,
  speakerLabel,
  copy,
  onSaved,
}: {
  meetingId: string;
  csrfToken: string;
  sourceSpeakerLabel: string;
  speakerLabel: string;
  copy: MeetingArchiveCopy;
  onSaved(sourceSpeakerLabel: string, displayName: string): void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(speakerLabel);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(speakerLabel);
  }, [editing, speakerLabel]);

  const cancel = () => {
    setDraft(speakerLabel);
    setError(false);
    setEditing(false);
  };

  const save = async () => {
    const displayName = draft.trim();
    if (!displayName) return;
    setSaving(true);
    setError(false);
    try {
      await renameMeetingSpeaker(
        csrfToken,
        meetingId,
        sourceSpeakerLabel,
        displayName,
      );
      onSaved(sourceSpeakerLabel, displayName);
      setEditing(false);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span className="archive-speaker">
        <UsersRound size={14} aria-hidden="true" />
        <span>{speakerLabel}</span>
        <button
          type="button"
          aria-label={`${copy.renameSpeaker}: ${speakerLabel}`}
          onClick={() => setEditing(true)}
        >
          <Pencil size={14} aria-hidden="true" />
        </button>
      </span>
    );
  }

  return (
    <form
      className="archive-speaker-editor"
      onSubmit={event => {
        event.preventDefault();
        void save();
      }}
    >
      <label>
        <span className="sr-only">{copy.speakerNameLabel}</span>
        <input
          type="text"
          value={draft}
          maxLength={80}
          autoFocus
          aria-label={copy.speakerNameLabel}
          aria-invalid={error}
          onChange={event => setDraft(event.currentTarget.value)}
        />
      </label>
      <button
        type="submit"
        disabled={saving || !draft.trim()}
        aria-label={copy.saveSpeaker}
        title={copy.saveSpeaker}
      >
        {saving
          ? <LoaderCircle className="progress-spinner" size={15} aria-hidden="true" />
          : <Save size={15} aria-hidden="true" />}
      </button>
      <button
        type="button"
        disabled={saving}
        aria-label={copy.cancel}
        title={copy.cancel}
        onClick={cancel}
      >
        <X size={15} aria-hidden="true" />
      </button>
      {error && <span className="archive-speaker-editor__error" role="alert">{copy.speakerSaveError}</span>}
    </form>
  );
}

function SummaryContent({ content, copy }: { content: MeetingSummaryContent; copy: MeetingArchiveCopy }) {
  const sections: Array<[string, string | string[]]> = [
    [copy.overview, stripSummaryEvidence(content.overview)],
    [copy.keyPoints, content.keyPoints.map(stripSummaryEvidence)],
    [copy.decisions, content.decisions.map(stripSummaryEvidence)],
    [copy.actionItems, content.actionItems.map(stripSummaryEvidence)],
    [copy.openQuestions, content.openQuestions.map(stripSummaryEvidence)],
  ];
  return (
    <div className="archive-summary">
      {sections.map(([title, value]) => {
        const items = Array.isArray(value) ? value : value ? [value] : [];
        if (items.length === 0) return null;
        return (
          <section key={title}>
            <h5>{title}</h5>
            {items.length === 1 && !Array.isArray(value) ? (
              <p>{items[0]}</p>
            ) : (
              <ul>{items.map((item, index) => <li key={`${title}-${index}`}>{item}</li>)}</ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function ArchiveMessage({
  icon,
  message,
  tone = "quiet",
  action,
}: {
  icon: React.ReactElement;
  message: string;
  tone?: "quiet" | "error";
  action?: React.ReactNode;
}) {
  return (
    <div className={`meeting-archive__message meeting-archive__message--${tone}`} role={tone === "error" ? "alert" : "status"}>
      {icon}
      <p>{message}</p>
      {action}
    </div>
  );
}

function MeetingState({ state, copy }: { state: string; copy: MeetingArchiveCopy }) {
  const normalized = normalizedMeetingState(state);
  const label = meetingStateLabel(state, copy);
  return <span className={`archive-state archive-state--${normalized}`}>{label}</span>;
}

function normalizedMeetingState(state: string): "recording" | "finalizing" | "ready" | "failed" | "processing" {
  const normalized = state.toLowerCase();
  if (normalized === "recording") return "recording";
  if (normalized === "finalizing") return "finalizing";
  if (normalized === "ready") return "ready";
  if (normalized === "failed") return "failed";
  return "processing";
}

function meetingStateLabel(state: string, copy: MeetingArchiveCopy): string {
  const normalized = normalizedMeetingState(state);
  if (normalized === "recording") return copy.statusRecording;
  if (normalized === "finalizing") return copy.statusFinalizing;
  if (normalized === "ready") return copy.statusReady;
  if (normalized === "failed") return copy.statusFailed;
  return copy.statusProcessing;
}

function normalizedSearch(value: string): string {
  return value.trim().toLocaleLowerCase().normalize("NFKC");
}

function normalizedProcessingState(state: string): "queued" | "processing" | "ready" | "failed" {
  const normalized = state.toLowerCase();
  if (normalized === "ready" || normalized === "completed" || normalized === "complete") return "ready";
  if (normalized === "failed" || normalized === "error") return "failed";
  if (normalized === "processing" || normalized === "running" || normalized === "in_progress") return "processing";
  return "queued";
}

function processingStatusLabel(
  state: ReturnType<typeof normalizedProcessingState>,
  copy: MeetingArchiveCopy,
): string {
  if (state === "ready") return copy.processingReady;
  if (state === "failed") return copy.processingFailed;
  if (state === "processing") return copy.processingRunning;
  return copy.processingQueued;
}

function localeLabel(locale: ArchiveLocale, copy: MeetingArchiveCopy): string {
  if (locale === "id") return copy.localeIndonesian;
  if (locale === "zh") return copy.localeChinese;
  if (locale === "th") return copy.localeThai;
  return copy.localeEnglish;
}

function formatMeetingDate(timestamp: number, language: TargetLanguage): string {
  const locale = language === "id"
    ? "id-ID"
    : language === "zh"
      ? "zh-CN"
      : language === "th"
        ? "th-TH"
        : "en-US";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

function formatTimestamp(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${minutes}:${String(seconds).padStart(2, "0")}`;
}
